import { useEffect, useState } from "react";
import { X, Boxes, Cable, GitMerge, Split, ArrowRight, Link2 } from "lucide-react";
import api from "@/lib/api";
import { COLOR_HEX } from "@/lib/modules";
import SpliceMap from "@/components/SpliceMap";
import AttachCablesDialog from "@/components/AttachCablesDialog";

export default function ClosureView({ closure, role, onClose, onOpenSplitter }) {
  const [data, setData] = useState(null);
  const [attach, setAttach] = useState(false);
  const loadData = () => api.get(`/closures/${closure.id}/contents`).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { loadData(); }, [closure.id]);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4" data-testid="closure-view">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Boxes size={18} className="text-purple-400" /> Mufa {closure.number}</h3>
          <button onClick={onClose} data-testid="closure-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        {!data ? <div className="p-6 text-slate-500 text-sm">Ładowanie…</div> : (
          <div className="overflow-y-auto p-5 space-y-5">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span>Typ: <b>{data.closure.closure_type || "-"}</b></span>
              <span>Tacki: <b className="font-mono">{data.closure.tray_count || "-"}</b></span>
              <span>Pojemność spawów: <b className="font-mono">{data.closure.splice_capacity || "-"}</b></span>
              <span className="ml-auto">Spawów: <b className="font-mono text-cyan-300">{data.splices.length}</b></span>
            </div>

            {/* Kable wchodzące/wychodzące */}
            <div>
              <div className="text-sm font-heading font-bold text-cyan-300 mb-2 flex items-center justify-between gap-2">
                <span className="flex items-center gap-2"><Cable size={15} /> Kable w mufie ({data.cables.length})</span>
                {role !== "odczyt" && (
                  <button type="button" onClick={() => setAttach(true)} data-testid="closure-attach-cables"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium">
                    <Link2 size={13} /> Dołącz / odłącz kable
                  </button>
                )}
              </div>
              {data.cables.length === 0 ? <div className="text-xs text-slate-500">Brak powiązanych kabli.</div> : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {data.cables.map((c) => (
                    <div key={c.id} className="rounded border border-slate-700 bg-slate-900/50 px-3 py-2 text-sm min-w-0" data-testid={`closure-cable-${c.id}`}>
                      <div className="font-mono text-white truncate" title={c.number}>{c.number}</div>
                      <div className="text-[11px] text-slate-500 truncate">{c.fiber_count || "-"} wł. · {c.cable_type || ""}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Rozpływ włókien (interaktywny) */}
            <SpliceMap closureId={closure.id} closureNumber={closure.number} />

            {/* Splittery w mufie */}
            {data.splitters.length > 0 && (
              <div>
                <div className="text-sm font-heading font-bold text-orange-300 mb-2 flex items-center gap-2"><Split size={15} /> Splittery w mufie ({data.splitters.length})</div>
                <div className="flex flex-wrap gap-2">
                  {data.splitters.map((s) => (
                    <button key={s.id} onClick={() => onOpenSplitter && onOpenSplitter(s.id)} data-testid={`closure-splitter-${s.id}`}
                      className="rounded border border-orange-700/50 bg-orange-950/20 hover:bg-orange-900/30 px-3 py-1.5 text-sm text-orange-200 flex items-center gap-1.5">
                      <Split size={13} /> {s.number} <span className="text-orange-400/70">{s.split_type}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Spawy */}
            <div>
              <div className="text-sm font-heading font-bold text-slate-200 mb-2 flex items-center gap-2"><GitMerge size={15} /> Spawy ({data.splices.length})</div>
              {data.splices.length === 0 ? <div className="text-xs text-slate-500">Brak spawów.</div> : (
                <div className="rounded border border-slate-700 divide-y divide-slate-800 max-h-72 overflow-y-auto">
                  {data.splices.map((s, i) => (
                    <div key={s.id || i} className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`closure-splice-${i}`}>
                      {s.fiber_color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLOR_HEX[s.fiber_color] || "#64748b" }} />}
                      <span className="font-mono text-slate-300">{s.cable_number || s.cable_id || "?"}{s.fiber_in ? `/${s.fiber_in}` : ""}</span>
                      <ArrowRight size={13} className="text-slate-600 shrink-0" />
                      <span className="text-slate-300 truncate">{s.cable_out_number ? `${s.cable_out_number}${s.fiber_out ? `/${s.fiber_out}` : ""}` : (s.leads_to || s.target_type || (s.target_splitter_id ? "Splitter" : "spaw") + (s.fiber_out ? ` (wł. ${s.fiber_out})` : ""))}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {attach && (
        <AttachCablesDialog closureId={closure.id} closureNumber={closure.number}
          onClose={() => setAttach(false)}
          onDone={() => { setAttach(false); loadData(); }} />
      )}
    </div>
  );
}
